import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';
import Page from '@pelckmans/elementis/components/page';
import Tabs from '@pelckmans/elementis/components/tabs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ModalContextProvider } from '../../contexts/ModalContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useRoutes } from '../../hooks/useRoutes';
import ApplicationLevelLayout from '../../layout/ApplicationLevelLayout';
import ContentBody from '../../layout/content/ContentBody';
import ContentHeader from '../../layout/content/ContentHeader';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../shared-components/elementis/hoc/withScrollThreshold';
import StudentGroupContextProvider from './context/StudentGroupContext';
import CurrentYearTab from './tabs/CurrentYearTab';
import PreviousYearTab from './tabs/PreviousYearTab';
import { toId } from './utils';

const MY_TEACHER_TABS = {
  CURRENT_SCHOOLYEAR: 'CURRENT_SCHOOLYEAR',
  PREVIOUS_SCHOOLYEAR: 'PREVIOUS_SCHOOLYEAR',
};

interface MyTeachersProps extends WithScrollThresholdProps {}

function MyTeachers({ pastThreshold }: MyTeachersProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getRoute } = useRoutes();
  const location = useLocation();

  const selectedTab = location.pathname.includes(getRoute('MY_TEACHERS.CURRENT_SCHOOLYEAR'))
    ? MY_TEACHER_TABS.CURRENT_SCHOOLYEAR
    : MY_TEACHER_TABS.PREVIOUS_SCHOOLYEAR;

  useDocumentTitle(t('PAGES.MY_TEACHERS.DOCUMENT_TITLE'));

  const onTabChange = useCallback(
    (tab: string) => {
      if (tab === selectedTab) return;

      const route =
        tab === MY_TEACHER_TABS.CURRENT_SCHOOLYEAR
          ? getRoute('MY_TEACHERS.CURRENT_SCHOOLYEAR')
          : getRoute('MY_TEACHERS.PREVIOUS_SCHOOLYEAR');

      navigate(route);
    },
    [getRoute, navigate, selectedTab],
  );

  return (
    <ModalContextProvider>
      <ApplicationLevelLayout>
        <Page>
          <Tabs selectedTab={selectedTab} onTabChange={onTabChange}>
            <ContentHeader
              cover={
                <Page.Header
                  density={pastThreshold ? 'compact' : 'normal'}
                  headerText={t('PAGES.MY_TEACHERS.HEADER.TITLE')}
                  tabs={
                    <Tabs.TabList aria-label={t('PAGES.MY_TEACHERS.HEADER.TAB_LIST.A11Y_LABEL')}>
                      <Tabs.Tab id={MY_TEACHER_TABS.PREVIOUS_SCHOOLYEAR}>
                        {t('PAGES.MY_TEACHERS.HEADER.TAB_LIST.TABS.PREVIOUS')}
                      </Tabs.Tab>
                      <Tabs.Tab id={MY_TEACHER_TABS.CURRENT_SCHOOLYEAR}>
                        {t('PAGES.MY_TEACHERS.HEADER.TAB_LIST.TABS.CURRENT')}
                      </Tabs.Tab>
                    </Tabs.TabList>
                  }
                />
              }
            />
            <ContentBody>
              <Tabs.TabsContent>
                {Object.keys(MY_TEACHER_TABS).map(tabKey => (
                  <Tabs.TabPanel key={tabKey} id={MY_TEACHER_TABS[tabKey as keyof typeof MY_TEACHER_TABS]}>
                    <Outlet />
                  </Tabs.TabPanel>
                ))}
              </Tabs.TabsContent>
            </ContentBody>
          </Tabs>
        </Page>
      </ApplicationLevelLayout>
    </ModalContextProvider>
  );
}

export function MyTeacherTabContent({ tab }: { tab: string }) {
  const { currentSchoolYear, previousSchoolYear } = useSchoolYear();

  const schoolYear = tab === MY_TEACHER_TABS.CURRENT_SCHOOLYEAR ? currentSchoolYear : previousSchoolYear;

  if (!schoolYear) return null;

  return (
    <StudentGroupContextProvider schoolYear={toId(schoolYear)}>
      {tab === MY_TEACHER_TABS.CURRENT_SCHOOLYEAR ? <CurrentYearTab /> : <PreviousYearTab />}
    </StudentGroupContextProvider>
  );
}

export default withScrollThreshold(MyTeachers);
